// Shared domain types for the Crypto Audit Service

// ─── Slug helper ─────────────────────────────────────────────────────────────
// Converts a project name to a clean URL slug: "Mandox Token" → "mandox-token"
export function toProjectSlug(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || 'project'
}

export type AuditStatus = 'pending' | 'in_review' | 'completed'
export type AuditVerdict = 'passed' | 'failed'
export type VulnSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info'

export interface SocialLinks {
  website?: string
  twitter?: string
  telegram?: string
  discord?: string
  github?: string
}

export interface ProjectProfile {
  _id: string
  name: string
  symbol: string
  chain: EvmChain
  contractAddress: string
  description: string
  bio?: string
  bannerUrl: string
  logoUrl: string
  social: SocialLinks
  packageTier: PackageTier | null
  audit: AuditResult | null
  creator: string
  createdAt: string
}

export type EvmChain =
  | 'ethereum'
  | 'bsc'
  | 'polygon'
  | 'arbitrum'
  | 'base'
  | 'optimism'
  | 'avalanche'

export interface Vulnerability {
  id: string
  title: string
  severity: VulnSeverity
  description: string
  resolved: boolean
}

// Per-checklist-item manual override: null = not set, true = pass, false = fail
export interface ChecklistOverride {
  state: 'pass' | 'fail' | null
  reason?: string // required when state === 'fail'
}

export interface AuditResult {
  status: AuditStatus
  verdict: AuditVerdict | null
  score: number | null // 1-100
  vulnerabilities: Vulnerability[]
  auditedAt: string | null
  summary: string
  // Manual checklist overrides keyed by checklist item id
  checklistOverrides?: Record<string, ChecklistOverride>
}

export type PackageTier = 'starter' | 'pro' | 'enterprise'

export interface AuditPackage {
  tier: PackageTier
  name: string
  priceUsd: number
  promoPriceUsd?: number
  turnaround: string
  highlight: boolean
  features: string[]
}

export const CHAIN_LABELS: Record<EvmChain, string> = {
  ethereum: 'Ethereum',
  bsc: 'BNB Chain',
  polygon: 'Polygon',
  arbitrum: 'Arbitrum',
  base: 'Base',
  optimism: 'Optimism',
  avalanche: 'Avalanche',
}

// Canonical security checklist — identical for every audited project.
// Each item maps to one or more vulnerability keywords so per-project
// pass/fail can be derived deterministically from audit findings.
export interface ChecklistItem {
  id: string
  label: string
  description: string
}

export const AUDIT_CHECKLIST: ChecklistItem[] = [
  { id: 'reentrancy',      label: 'Reentrancy protection',        description: 'External calls cannot re-enter and drain the contract.' },
  { id: 'access_control',  label: 'Access control',               description: 'Privileged functions are restricted to authorized roles.' },
  { id: 'mint_supply',     label: 'Supply / mint integrity',      description: 'Token supply cannot be arbitrarily inflated by the owner.' },
  { id: 'overflow',        label: 'Arithmetic safety',            description: 'No integer overflow or underflow in critical math.' },
  { id: 'oracle',          label: 'Oracle / price manipulation',  description: 'Price feeds are resistant to flash-loan manipulation.' },
  { id: 'centralization',  label: 'Centralization & rug risk',    description: 'No single key can drain funds without safeguards.' },
  { id: 'slippage',        label: 'Slippage & MEV protection',    description: 'Swaps enforce minimum output and resist sandwich attacks.' },
  { id: 'zero_address',    label: 'Input validation',             description: 'Critical setters validate addresses and parameters.' },
  { id: 'gas_dos',         label: 'Gas / DoS resistance',         description: 'No unbounded loops that can exceed the block gas limit.' },
  { id: 'best_practice',   label: 'Code quality & best practices', description: 'Follows current Solidity standards and conventions.' },
]

// Keyword map used to derive which checklist item a finding affects.
export const CHECK_KEYWORDS: Record<string, string[]> = {
  reentrancy:     ['reentran'],
  access_control: ['access control', 'setfee'],
  mint_supply:    ['mint', 'supply'],
  overflow:       ['overflow', 'underflow', 'arithmetic'],
  oracle:         ['oracle', 'price'],
  centralization: ['drain', 'admin', 'emergencywithdraw', 'centraliz'],
  slippage:       ['slippage', 'sandwich'],
  zero_address:   ['zero-address', 'zero address'],
  gas_dos:        ['loop', 'gas limit', 'unbounded'],
  best_practice:  ['redundant', 'timestamp', 'unchecked return'],
}

export const SEVERITY_META: Record<
  VulnSeverity,
  { label: string; color: string; bg: string; border: string }
> = {
  critical: { label: 'Critical', color: '#ff5470', bg: 'rgba(255,84,112,0.12)', border: 'rgba(255,84,112,0.35)' },
  high: { label: 'High', color: '#ff8a5b', bg: 'rgba(255,138,91,0.12)', border: 'rgba(255,138,91,0.35)' },
  medium: { label: 'Medium', color: '#f5c451', bg: 'rgba(245,196,81,0.12)', border: 'rgba(245,196,81,0.35)' },
  low: { label: 'Low', color: '#6ea8fe', bg: 'rgba(110,168,254,0.12)', border: 'rgba(110,168,254,0.35)' },
  info: { label: 'Info', color: '#9aa7c2', bg: 'rgba(154,167,194,0.12)', border: 'rgba(154,167,194,0.3)' },
}
